import {
    DefaultEventsMap,
    DeviceType,
    EventDataType,
    IEventManagerWithAsync,
    IModule,
    IView,
    ListenerTypeWithID, SystemComponentType
} from '@gongxigames/framework/core/types';
import {ModuleState, ModuleStateConstructor} from '@gongxigames/framework/slot/fragments/module/ModuleState';
import {StatedDebug} from '@gongxigames/framework/slot/fragments/module/StatedDebug';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {CoreEventsMap} from '@gongxigames/framework/core/events';
import {inject, injectable, Installer} from '@gongxigames/framework/core/installer';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';
import {System} from '@gongxigames/framework/core/services/system/System';
import {ILogic} from '@gongxigames/framework/slot/types';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {Device, ISoundPlayer, SlotEventAdapter} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';

export interface ModuleStatesMap<V extends IView | null, M extends Module<V, E>, E = DefaultEventsMap> {
    [name: string]: ModuleState<V, M, E>;
}

export type ModuleStatesConstructorsByName = Record<string, ModuleStateConstructor<any, any>>;

/**
 * Class for every game module to be extended from
 * handles views and module-to-module events.
 * also changes the states of current module and handles events from view
 */
@injectable()
export class Module<V extends IView<EventsMap> | null, EventsMap = DefaultEventsMap> implements IModule {
    public type: SystemComponentType = 'module';

    private static _debugInfo: StatedDebug = new StatedDebug();

    protected _view?: V;
    protected _viewName?: symbol|string;

    protected _states!: ModuleStatesMap<V, Module<V, EventsMap>, EventsMap>;

    protected _currentState!: ModuleState<V, Module<V, EventsMap>, EventsMap>;

    @inject(CoreDI.Installer)
    protected _installer!: Installer;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync<EventsMap>;

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    @inject(CoreDI.SlotEventAdapter)
    private _slotEventAdapter!: SlotEventAdapter;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(DI.SoundPlayer)
    protected _soundPlayer!: ISoundPlayer;

    @inject(CoreDI.Settings)
    protected _settings!: SlotSettings;

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    public init(): void {
        this._states = {};

        this.setupViews();
        this.setupStates();
        this.setupEvents();
    }

    protected setupViews(): void {

    }

    protected setupStates(): void {

    }

    protected setupEvents(): void {
        (this._eventManager as IEventManagerWithAsync<CoreEventsMap>).on(
            'General.ResourcesLoading.LOADED_ALL_RESOURCES',
            this.onResourcesLoaded.bind(this
            ), this);

        /* develblock:start */
        (this._eventManager as IEventManagerWithAsync).on('General.HotReload.ViewsUpdated', this.onViewUpdated.bind(this), this);
        /* develblock:end */
    }

    public restore(): void {}

    public gotoState(name: string): void {
        const fromState: string = this._currentState ? this._currentState.name : '';

        this._currentState && this._currentState.onLeaveState();
        this._currentState = this._states[name];
        this._currentState.onEnterState();
        Module._debugInfo.setModuleState(this.constructor.name, this._currentState.name);

        consoleLogger.log(`❱[${this.constructor.name}] State: ${fromState} => ${this.currentState.name}`);
    }

    public destroy(): void {
        consoleLogger.log(`Module ${this.constructor.name} destroyed`);
    }

    protected onResourcesLoaded(): void {
        this._view && this.view!.onResourcesLoaded();
    }

    protected setView(viewName: symbol|string): void {
        this._view = this._installer.getComponent<V>(viewName);
        this._viewName = viewName;
    }

    protected addState(state: ModuleState<V, Module<V, EventsMap>, EventsMap>): void {
        this._states[state.name] = state;
    }

    protected addStates(states: ModuleStatesConstructorsByName): void {
        for (const [stateName, StateConstructor] of Object.entries(states)) {
            this.addState(new StateConstructor(this, stateName));
        }
    }

    protected on<K extends keyof EventsMap>(id: K, callback: ListenerTypeWithID<K, EventsMap[K]>, priority?: number): void {
        this._eventManager.on(id as any, callback.bind(this, id) as any, this, priority);
    }

    protected off<K extends keyof EventsMap>(id: K): void {
        this._eventManager.off(id as any, this);
    }

    protected onAsync<K extends keyof EventsMap>(id: K, callback: ListenerTypeWithID<K, EventsMap[K]>, priority?: number): void {
        this._eventManager.onAsync(id as any, callback.bind(this, id) as any, this, priority);
    }

    protected offAssync<K extends keyof EventsMap>(id: K): void {
        this._eventManager.offAsync(id as any, this);
    }

    protected onCurrentStateHandle<K extends keyof EventsMap>(id: K, priority?: number): void {
        this.on(id, this.onCurrentStateHandleEvent as any, priority);
    }

    protected onAsyncCurrentStateHandle<K extends keyof EventsMap>(id: K, priority?: number): void {
        this.onAsync(id, this.onCurrentStateHandleEvent as any, priority);
    }

    protected onAll<K extends keyof EventsMap>(ids: K[], listener: ListenerTypeWithID<K, EventsMap[K]>, priority?: number): void {
        ids.forEach(id => this.on(id, listener, priority));
    }

    protected onView<K extends keyof EventsMap>(id: K, callback: ListenerTypeWithID<K, EventsMap[K]>, priority?: number): void {
        this._view && this.view!.on(id as any, callback.bind(this, id) as any, this, priority);
    }

    protected onViewCurrentStateHandle<K extends keyof EventsMap>(id: K, priority?: number): void {
        this.onView(id, this.onCurrentStateHandleEvent as any, priority);
    }

    protected onCurrentStateHandleEvent<K extends keyof EventsMap>(id: K, ...args: EventDataType<EventsMap[K]>): void {
        this.currentState?.handle(id, ...args);

        if (!this.currentState) {
            consoleLogger.warn(`[${this.constructor.name}] Does not have any state added.`);
        }
    }

    public hasView(): boolean {
        return this._view !== undefined;
    }

    public get view(): V {
        if (!this._view) {
            throw new Error('This module does not have a View instance!');
        }

        return this._view;
    }

    public getEventManager<E = EventsMap>(): IEventManagerWithAsync<E> {
        return this._eventManager as IEventManagerWithAsync;
    }

    public get currentState(): ModuleState<V, Module<V, EventsMap>, EventsMap> {
        return this._currentState;
    }

    public get system(): System {
        return this._system;
    }

    public get eventManager(): IEventManagerWithAsync<EventsMap> {
        return this._eventManager;
    }

    public get updateManager(): UpdateManager {
        return this._updateManager;
    }

    public get settings(): SlotSettings {
        return this._settings;
    }

    public get soundPlayer(): ISoundPlayer {
        return this._soundPlayer;
    }

    public get logic(): ILogic {
        return this._logic;
    }

    public getLogic<T extends ILogic>(): T {
        return this._logic as T;
    }

    public get resourceManager(): ResourceManager {
        return this._resourceManager;
    }

    public get slotEventAdapter(): SlotEventAdapter {
        return this._slotEventAdapter;
    }

    protected isPlatform(platform: DeviceType): boolean {
        return this._settings.get('platform') === platform;
    }

    protected isPlatformMobile(): boolean {
        return this.isPlatform(DeviceType.MOBILE);
    }

    protected isPlatformDesktop(): boolean {
        return this.isPlatform(DeviceType.DESKTOP);
    }

    /* develblock:start */
    private onViewUpdated(): void {
        if (this._viewName) {
            this.setView(this._viewName);
        }
    }
    /* develblock:end */
}